import type { KeyboardEvent } from 'react';

import type { CheckboxTheme, IceFunction, IconPosition, StringOrNode } from '@snowit/icetypes';
import type { A11yEvent } from '@snowit/icetypes/events';

import useA11y from '@snowit/hooks/useA11y';
import { Keyboard } from '@snowit/iceconst';
import { makeCSS } from '@snowit/utils/misc';
import testId from '@snowit/utils/testId';

import Typo from '../Typography/Typo';

import cx from 'classnames';
import s from './Checkbox.module.scss';

export interface ICheckbox {
  theme?: CheckboxTheme;
  squarePosition?: IconPosition;
  name?: string;
  label?: string;
  sublabel?: string;
  content?: StringOrNode;
  hint?: string;
  value: string | number;
  squareSize?: number;
  checked?: boolean;
  included?: boolean;
  disabled?: boolean;
  required?: boolean;
  error?: boolean;
  errorMsg?: string;
  onChange: IceFunction;
  id?: string;
  classNames?: {
    wrapper?: string;
    input?: string;
    wrapLabel?: string;
    label?: string;
    sublabel?: string;
    square?: string;
    hint?: string;
    errorMsg?: string;
  };
  test?: string;
}

const Checkbox = (props: ICheckbox) => {
  const {
    theme = 'default',
    squarePosition = 'after',
    name,
    label,
    sublabel,
    content,
    hint,
    value,
    squareSize = 1.5,
    checked,
    included,
    disabled,
    required,
    error,
    errorMsg,
    onChange,
    classNames,
    test,
  } = props;
  const a11yH = useA11y();

  const id = a11yH.makeId(props.id);
  const hintId = a11yH.makeId(props.id, 'hint');
  const errorId = a11yH.makeId(props.id, 'error');

  const handleChange = (event: A11yEvent<HTMLInputElement>) => {
    event.stopPropagation();
    if (disabled || included) return;
    onChange({
      name,
      value,
      id,
      event,
    });
  };

  const handleKeydown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== Keyboard.ENTER) return;
    handleChange(event);
  };

  const returnLabel = () => {
    if (!label) return null;
    return (
      <Typo
        className={cx(s.label, classNames?.label)}
        text={label}
        tag={'label'}
        size={'df'}
        color={'text'}
        a11y={{ htmlFor: id }}
      />
    );
  };

  const renderSublabel = () => {
    if (!sublabel) return null;
    return (
      <Typo
        className={classNames?.sublabel}
        text={sublabel}
        tag={'p'}
        size={'sm'}
        color={'text-l'}
      />
    );
  };

  const renderContent = () => {
    if (!label && !content) return null;
    if (!content) {
      return (
        <div className={cx(s.wrapLabel, classNames?.wrapLabel)}>
          {returnLabel()}
          {renderSublabel()}
        </div>
      );
    }
    if (typeof content === 'function') return content();
    return <div className={classNames?.wrapLabel}>{content}</div>;
  };

  const renderSquare = () => {
    let ariaDescribedby: string | undefined;
    if (errorMsg) {
      ariaDescribedby = errorId;
    } else if (hint) {
      ariaDescribedby = hintId;
    }

    return (
      <input
        className={cx(s.square, classNames?.square)}
        style={makeCSS({ '--square-size': squareSize })}
        type='checkbox'
        id={id}
        value={value}
        checked={checked}
        name={name}
        onChange={handleChange}
        onKeyDown={handleKeydown}
        disabled={disabled}
        aria-describedby={ariaDescribedby}
        aria-invalid={error}
        aria-label={label}
        {...testId(test)}
      />
    );
  };

  const renderHint = () => {
    if (!hint || errorMsg) return null;
    return (
      <Typo
        id={hintId}
        className={classNames?.hint}
        text={hint}
        tag={'p'}
        size={'sm'}
        color={'text-l'}
      />
    );
  };

  const renderErrorMsg = () => {
    if (!error || !errorMsg) return null;
    return (
      <Typo
        id={errorId}
        className={classNames?.errorMsg}
        text={errorMsg}
        tag={'p'}
        size={'sm'}
        color={'error'}
      />
    );
  };

  return (
    <div className={cx(s.wrapper, classNames?.wrapper)}>
      <div
        className={cx(s.input, classNames?.input, {
          [s[theme]]: true,
          [s[squarePosition]]: true,
          [s.checked]: checked || included,
          [s.disabled]: disabled,
          [s.required]: required,
          [s.error]: error,
        })}
      >
        {renderContent()}
        {renderSquare()}
      </div>
      {renderHint()}
      {renderErrorMsg()}
    </div>
  );
};

export default Checkbox;
